export const videoDialog = () => {
    const videoDialogs = document.querySelectorAll('.video-dialog');
    videoDialogs?.forEach((v) => {
        const info: {
            iframeUrl?: string,
            /** JSX */
            thumbnail?: unknown,
            /** Not necessarily accurate (thumbnail aspect ratio may differ) */
            aspectRatio?: Promise<[number, number] | undefined>
        } = {};
        
        const href = (v as HTMLElement).dataset["videoUrl"];
        
        if (!href)
            return;

        const url = new URL(href, document.baseURI);

        if (
            (url.hostname === 'www.youtube.com'  && url.pathname.startsWith('/embed/')) ||
            (url.hostname === 'player.vimeo.com' && url.pathname.startsWith('/video/'))
        ) {
            if (!url.searchParams.has('autoplay'))
                url.searchParams.set('autoplay', '1');
            info.iframeUrl = url.href;
        }

        let source: 'youtube' | 'vimeo' | 'other' = 'other';
        if (['www.youtube.com', 'youtube.com', 'youtu.be'].includes(url.hostname)) {
            source = 'youtube';
            let code: string | undefined | null;
            if (url.pathname.startsWith('/watch'))
                code = url.searchParams.get('v');
            if (url.pathname.startsWith('/embed'))
                code = url.pathname.match(/\/embed\/([^\/]+)/)?.[1];
            if (url.hostname === 'youtu.be')
                code = url.pathname.match(/\/([^\/]+)/)?.[1];
    
            if (code) {
                info.iframeUrl ??= `https://www.youtube-nocookie.com/embed/${code}?autoplay=1&enablejsapi=1`;
                const aspectRatioDeferred = deferredPromise<[number, number] | undefined>();
                info.aspectRatio = aspectRatioDeferred.promise;
                info.thumbnail =
                    `<picture>
                        <img
                            class='video-embed__image'
                            alt=''
                            src=${`https://i.ytimg.com/vi/${code}/maxresdefault.jpg`}
                            ref=${async img => {
                                aspectRatioDeferred.resolve(
                                    await imgRefSetDimensions(img)
                                );
                            }}
                        />
                    </picture>`;
            }
        }

        if (url.hostname === 'vimeo.com') {
            source = 'vimeo';
            let code = url.pathname.match(/\/([^\/]+)/)?.[1];
            if (code)
                info.iframeUrl ??= `https://player.vimeo.com/video/${code}?autoplay=1`;
        }

        const playBtn = v.querySelector('.play-button');
        const closeBtn = v.querySelector('.close-button');

        const dialog = v.querySelector('dialog');
        if (!dialog)
            return;

        const iframe = dialog?.querySelector('iframe');
        if (!iframe)
            return;

        playBtn?.addEventListener('click', () => {
            dialog.showModal();

            if (info.iframeUrl) {
                iframe.src ||= info.iframeUrl;
            }

            if (source == 'youtube') {
                iframe.contentWindow?.postMessage(JSON.stringify({
                    event: 'command',
                    func: 'playVideo'
                }), '*')
            } else if (source == 'vimeo') {
                iframe.contentWindow?.postMessage(JSON.stringify({
                    method: 'play'
                }), '*')
            }
            else {
                iframe.removeAttribute('src');
            }
        });
        
        dialog.addEventListener('close', async () => {

            if (source == 'youtube') {
                iframe.contentWindow?.postMessage(JSON.stringify({
                    event: 'command',
                    func: 'pauseVideo'
                }), '*')
            } else if (source == 'vimeo') {
                iframe.contentWindow?.postMessage(JSON.stringify({
                    method: 'pause'
                }), '*')
            }
            else {
                iframe.removeAttribute('src');
            }
        });

        dialog.addEventListener('click', () => dialog.close());
        closeBtn?.addEventListener('click', () => dialog.close());
    });
};

const deferredPromise = <Resolved = unknown, Rejected = never>() => {
	const result: {
		resolve: (x: Resolved) => void,
		reject:  (x: Rejected) => void,
		promise: Promise<Resolved | Rejected>
	} = {} as any;
	result.promise = new Promise((resolve, reject) => {
		result.resolve = resolve;
		result.reject  = reject;
	});
	return result;
};

const imgRefSetDimensions = async (img?: HTMLImageElement): Promise<[number, number] | undefined> => {
	if (!img)
		return;

	if (img.complete)
		return useNaturalImageDimensions(img);

	return new Promise(resolve => {
		img.addEventListener('load', () => {
			resolve(useNaturalImageDimensions(img));
		}, { once: true });
	});
};

const useNaturalImageDimensions = (img: HTMLImageElement) =>
	[img.width, img.height] = [img.naturalWidth, img.naturalHeight];
